import React, {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
  FormEvent,
} from "react";
import { useAccount, useConnect, useDisconnect, useSwitchChain } from "wagmi";
import { ethers } from "ethers";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  Form,
  Input,
  Label,
  Text,
  TextVariant,
  TextWeight,
  View,
} from "@pufferfinance/puffer-ui-components";
import { unifiTestnet } from "../wagmi";
import { useAppToast } from "../hooks/useAppToast";
import { CONTRACTS } from "../constants/contracts";
import { AP2_SERVICE_URL } from "../constants/network";
import { getReadProvider } from "../utils/provider";
import styles from "./USDCBalance.module.scss";

export interface USDCBalanceRef {
  refreshBalance: () => void;
}

export const USDCBalance = forwardRef<USDCBalanceRef, object>(
  (_props, ref) => {
    const { showError, showSuccess, showWalletError } = useAppToast();
    const { address, chainId, isConnected } = useAccount();
    const { connect, connectors, isPending } = useConnect({
      mutation: {
        onError: (error) => {
          showWalletError(error, "Wallet connect");
        },
      },
    });
    const { disconnect } = useDisconnect();
    const { switchChain, isPending: isSwitchingChain } = useSwitchChain();

    const [balance, setBalance] = useState<string>("");
    const [isLoadingBalance, setIsLoadingBalance] = useState(false);
    const [mintAmount, setMintAmount] = useState("");
    const [isMinting, setIsMinting] = useState(false);

    // Check if user is on the correct network
    const isCorrectNetwork = chainId === unifiTestnet.id;
    const shouldShowNetworkError = isConnected && !isCorrectNetwork;

    const handleSwitchNetwork = useCallback(async () => {
      try {
        await switchChain({ chainId: unifiTestnet.id });
        showSuccess("Successfully switched to Unifi Testnet!");
      } catch (error) {
        console.error("Failed to switch network:", error);
        showWalletError(error, "Network switch");
      }
    }, [switchChain, showWalletError, showSuccess]);

    const fetchBalance = useCallback(async () => {
      if (!address || !isConnected || !isCorrectNetwork) return;

      try {
        setIsLoadingBalance(true);
        const provider = getReadProvider();
        const tokenAbi = [
          "function balanceOf(address owner) view returns (uint256)",
          "function decimals() view returns (uint8)",
        ];
        const tokenContract = new ethers.Contract(
          CONTRACTS.USDC,
          tokenAbi,
          provider,
        );

        const [balanceRaw, decimals] = await Promise.all([
          tokenContract.balanceOf(address),
          tokenContract.decimals(),
        ]);

        const balanceFormatted = ethers.formatUnits(balanceRaw, decimals);
        setBalance(balanceFormatted);
      } catch (error) {
        console.error("Error fetching balance:", error);
        setBalance("Error");
      } finally {
        setIsLoadingBalance(false);
      }
    }, [address, isConnected, isCorrectNetwork]);

    useEffect(() => {
      if (isConnected && address && isCorrectNetwork) {
        fetchBalance();
      } else {
        setBalance("");
      }
    }, [isConnected, address, isCorrectNetwork, fetchBalance]);

    // Expose fetchBalance to parent components
    useImperativeHandle(
      ref,
      () => ({
        refreshBalance: fetchBalance,
      }),
      [fetchBalance],
    );

    const handleMint = useCallback(
      async (
        _formData: { [k: string]: FormDataEntryValue },
        _e: FormEvent<HTMLFormElement>,
      ) => {
        if (!address || !mintAmount) {
          showError("Please enter mint amount");
          return;
        }

        if (!isCorrectNetwork) {
          showError("Please switch to Unifi Testnet to mint USDC");
          return;
        }

        try {
          setIsMinting(true);

          const res = await fetch(`${AP2_SERVICE_URL}/mint-usdc`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userAddress: address, amount: mintAmount }),
          });

          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || `Mint failed (${res.status})`);
          }

          showSuccess(`Successfully minted ${mintAmount} USDC!`);
          await fetchBalance();
          setMintAmount("");
        } catch (error) {
          console.error("Minting failed:", error);
          showWalletError(error, "Mint USDC");
        } finally {
          setIsMinting(false);
        }
      },
      [
        address,
        mintAmount,
        isCorrectNetwork,
        fetchBalance,
        showError,
        showSuccess,
        showWalletError,
      ],
    );

    return (
      <div className={styles.blueCardWrapper}>
        <View flexDirection="column" className={styles.walletCard}>
          {isConnected ? (
            <>
              {/* Wallet indicator bar */}
              <View
                justifyContent="space-between"
                alignItems="center"
                className={styles.walletIndicator}
              >
                <Text
                  variant={TextVariant.Body2}
                  weight={TextWeight.Medium}
                  className={styles.addressText}
                >
                  {"🐡"} {address?.slice(0, 6)}...{address?.slice(-4)}
                </Text>
                <Button
                  onPress={() => disconnect()}
                  variant={ButtonVariant.Border}
                  size={ButtonSize.Small}
                >
                  Disconnect
                </Button>
              </View>

              {/* Info cards */}
              <View gap={8} className={styles.detailCards}>
                <View flexDirection="column" className={styles.detailCard}>
                  <Text
                    variant={TextVariant.Body2}
                    color="var(--secondary-cadet-50)"
                    weight={TextWeight.Medium}
                  >
                    Network
                  </Text>
                  <Text
                    variant={TextVariant.Body2}
                    weight={TextWeight.SemiBold}
                    color={
                      isCorrectNetwork ? "var(--success-60)" : "var(--error-60)"
                    }
                  >
                    {isCorrectNetwork ? "Unifi Testnet" : `Chain ${chainId}`}
                  </Text>
                </View>
                <View flexDirection="column" className={styles.detailCard}>
                  <View justifyContent="space-between" alignItems="center">
                    <Text
                      variant={TextVariant.Body2}
                      color="var(--secondary-cadet-50)"
                      weight={TextWeight.Medium}
                    >
                      USDC Balance
                    </Text>
                    <Button
                      onPress={fetchBalance}
                      isDisabled={isLoadingBalance || !isCorrectNetwork}
                      variant={ButtonVariant.Ghost}
                      size={ButtonSize.Small}
                    >
                      ↻
                    </Button>
                  </View>
                  {isLoadingBalance ? (
                    <Text
                      variant={TextVariant.Body2}
                      color="var(--secondary-cadet-50)"
                    >
                      Loading...
                    </Text>
                  ) : (
                    <span className={styles.balanceValue}>
                      {balance
                        ? `${parseFloat(balance).toLocaleString()}`
                        : "0"}{" "}
                      USDC
                    </span>
                  )}
                </View>
              </View>

              {/* Network error */}
              {shouldShowNetworkError && (
                <View className={styles.networkError}>
                  <View justifyContent="space-between" alignItems="center">
                    <View flexDirection="column">
                      <Text
                        variant={TextVariant.Body1}
                        weight={TextWeight.SemiBold}
                        color="var(--error-60)"
                      >
                        Wrong Network
                      </Text>
                      <Text variant={TextVariant.Body2} color="var(--error-40)">
                        Please switch to Unifi Testnet to use this application.
                      </Text>
                    </View>
                    <Button
                      onPress={handleSwitchNetwork}
                      isPending={isSwitchingChain}
                      variant={ButtonVariant.Border}
                      size={ButtonSize.Small}
                    >
                      {isSwitchingChain ? "Switching..." : "Switch Network"}
                    </Button>
                  </View>
                </View>
              )}

              {/* Mint form - vertical flow */}
              <Form
                onSubmit={handleMint}
                flexDirection="column"
                className={styles.mintSection}
              >
                <View flexDirection="column" gap={3}>
                  <Label htmlFor="mintAmount" className={styles.mintLabel}>
                    Mint Amount
                  </Label>
                  <Input
                    type="number"
                    id="mintAmount"
                    name="mintAmount"
                    value={mintAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setMintAmount(e.target.value)
                    }
                    placeholder="0.0"
                    min={0}
                    step={0.000001}
                    className={styles.input}
                    required
                  />
                </View>
                <Button
                  type="submit"
                  isDisabled={isMinting || !mintAmount || !isCorrectNetwork}
                  variant={ButtonVariant.CTA}
                  size={ButtonSize.Large}
                  display="flex"
                >
                  {isMinting ? "Minting..." : "Mint USDC"}
                </Button>
              </Form>
            </>
          ) : (
            <View
              flexDirection="column"
              alignItems="center"
              className={styles.emptyState}
              gap={8}
            >
              <Text
                variant={TextVariant.Body1}
                color="var(--secondary-cadet-50)"
              >
                Connect your wallet to get started
              </Text>
              {connectors.map((connector) => (
                <Button
                  key={connector.uid}
                  onPress={() => connect({ connector })}
                  isDisabled={isPending}
                  variant={ButtonVariant.CTA}
                  size={ButtonSize.Large}
                  display="flex"
                >
                  Connect {connector.name}
                </Button>
              ))}
            </View>
          )}
        </View>
      </div>
    );
  },
);

USDCBalance.displayName = "USDCBalance";
